//core
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import arePropsEqual from "react-fast-compare";
import format from "date-fns/format";
import { endOfDay, startOfDay } from "date-fns";
import {
  Box,
  IconButton,
  Badge,
  Tooltip,
  Collapse,
  useMediaQuery,
} from '@mui/material';
import Button from "../../../../../UI/Buttons/Button";

import FilterAltIcon from '@mui/icons-material/FilterAlt';
import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import StarIcon from '@mui/icons-material/Star';

//helpers
import { countValuesInObj, countValuesInObjWithExceptions } from "../../../../../../helpers/countValuesInObj";

//styles
import { useStyles } from "./styles";

//selectors
import {
  selectListData,
  selectOptionsData,
  selectListDataColumnsSort,
} from "../../../../../../store/admin/reports/cashier_statistic_report/selectors";

//hooks
import { useCashierStatisticReport } from "../../../../../../store/admin/reports/cashier_statistic_report/useCashierStatisticReport";
import { usePermissions } from "../../../../../../store/common/configuration/usePermissions";

//context
import { GlobalContext } from "../../../../../../context/GlobalContext";

//components
import { ICashierStatReportFilterDataType, OrderType } from "../../types";
import MuiDrawerWrapper from "../../../../../UI/MuiDrawerWrapper";
import VisibleFilter from "./components/VisibleFilter";
import SortMenu from "../../../../../common/SortMenu";
import Filter from "./components/Filter";
import SelectedFiltersWrapper from "./components/SelectedFiltersWrapper";
import PermissionsWrapper from "../../../../../common/PermissionsWrapper";
import FilterTabs from "./components/FilterTabs";
import GenerateGroup from "../GenerateGroup";
import {
  initialCashierStatReportFilterData as initialFilterData
} from "../DesktopReport";
import TableSettings from "../../../../../common/FiltersComponents/TableSettings";
import { TableColumnsItem } from "../../../../../common/FiltersComponents/TableSettings/types";
import { reportStorageProps } from "../../constants";
import SavedReportsList from "../SavedReportsList";
import { MuiSwitch } from "../../../../../UI/FormFields/MuiSwitch";

export interface IMyFilterItem {
  [key: string]: any;
}

interface IDrawerFilterProps {
  initialFilter: ICashierStatReportFilterDataType;
  setUpdateFilter: React.Dispatch<React.SetStateAction<ICashierStatReportFilterDataType>>;
  children?: React.ReactNode | null;
  order: OrderType;
  setOrder: (order: OrderType) => void;
  showOrderOnlyMobile?: boolean;
  onDownload: () => void;
  onGenerateReport: () => void;
  setGenerateReport: React.Dispatch<React.SetStateAction<boolean>>;
  columns: TableColumnsItem[];
  defaultColumns: TableColumnsItem[];
  setColumns: React.Dispatch<React.SetStateAction<TableColumnsItem[]>>;
  selectedFilterId: string;
  setSelectedFilterId: React.Dispatch<React.SetStateAction<string>>;
  selectedMyReportId: string;
  setSelectedMyReportId: React.Dispatch<React.SetStateAction<string>>;
  savedReportsData: null | any;
  setSavedReportsData: React.Dispatch<React.SetStateAction<null | any>>;
  savedReportsFavoritesIds: null | number[];
  setSavedReportsFavoritesIds: React.Dispatch<React.SetStateAction<null | number[]>>;
  savedReportsDefaultId: null | number[];
  setSavedReportsDefaultId: React.Dispatch<React.SetStateAction<null | number[]>>;
  savedReportsDefaultItem: any | null;
  getSavedReports: () => void;
  savedReportsFavoritesItem: any | null;
  getSavedReportsDefault: () => void;
  resetToDefault: () => void;
  toggleAllStats: boolean;
  setToggleAllStats: React.Dispatch<React.SetStateAction<boolean>>;
}

const DrawerFilter: React.FC<IDrawerFilterProps> = ({
  initialFilter,
  setUpdateFilter,
  children = null,
  order,
  setOrder,
  onDownload,
  onGenerateReport,
  setGenerateReport,
  columns,
  setColumns,
  defaultColumns,
  setSelectedFilterId,
  selectedFilterId,
  selectedMyReportId,
  setSelectedMyReportId,
  savedReportsData,
  setSavedReportsData,
  savedReportsFavoritesIds,
  setSavedReportsFavoritesIds,
  savedReportsDefaultId,
  setSavedReportsDefaultId,
  savedReportsDefaultItem = null,
  getSavedReports,
  savedReportsFavoritesItem = null,
  getSavedReportsDefault,
  resetToDefault,
  toggleAllStats,
  setToggleAllStats,
}): JSX.Element => {
  const { t } = useTranslation();
  const styles = useStyles();
  const { hasPermissions } = usePermissions();
  const mobile = useMediaQuery('(max-width:767px)');

  const filterExceptions = mobile
    ? ['period', 'sent_from', 'sent_to',]
    : ['period', 'sent_from', 'sent_to', 'group_by'];

  const context = useContext(GlobalContext);

  const { getOptions } = useCashierStatisticReport();

  const optionsData = useSelector(selectOptionsData);
  const dataReport = useSelector(selectListData);
  const sortingColumns = useSelector(selectListDataColumnsSort);

  const [openDrawer, setOpenDrawer] = useState(false);
  const [openFavoritesDrawer, setOpenFavoritesDrawer] = useState(false);
  const [openCollapse, setOpenCollapse] = useState(true);
  const [showVisibleFilter, setShowVisibleFilter] = useState(true);
  const [activeField, setActiveField] = useState('');

  useEffect(() => {
    if (!optionsData) {
      getOptions({});
    }
  }, [optionsData]);

  useEffect(() => {
    if (mobile && !!dataReport) {
      setShowVisibleFilter(false)
    }
  }, [dataReport]);

  useEffect(() => {
    if (!mobile) {
      setShowVisibleFilter(true);
    } else {
      if (!!dataReport) {
        setShowVisibleFilter(false)
      }
    }
  }, [mobile]);

  const handleClearFilters = useCallback(() => {
    setUpdateFilter({
      ...initialFilterData,
      sent_from: format(startOfDay(new Date(initialFilterData.sent_from)), "yyyy-MM-dd HH:mm:ss"),
      sent_to: format(endOfDay(new Date(initialFilterData.sent_to)), "yyyy-MM-dd HH:mm:ss"),
    });
    context.setAdmCashierStatisticReportFilter({
      ...initialFilterData,
      sent_from: format(startOfDay(new Date(initialFilterData.sent_from)), "yyyy-MM-dd HH:mm:ss"),
      sent_to: format(endOfDay(new Date(initialFilterData.sent_to)), "yyyy-MM-dd HH:mm:ss"),
    })

    //clear selectedFilterId
    setSelectedFilterId('');

    setTimeout(() => {
      setGenerateReport((prevState) => {
        return !prevState
      });
    }, 100)
  }, [setUpdateFilter]);

  const handleOpenDrawer = () => {
    setOpenDrawer(true);
  };

  const handleOpenFavoritesDrawer = () => {
    setOpenFavoritesDrawer(true);
  };

  const handleSetActiveField = (field: string) => {
    setActiveField(field);
    setOpenDrawer(true);
  };

  const handleToggleCollapse = () => {
    setOpenCollapse(prevState => !prevState);
  };

  const onDeleteOption = useCallback((id: string | number, fieldName: string, indexToRemove: number) => {
    setUpdateFilter( (prevState) => {
      // @ts-ignore
      const newFieldData = reportStorageProps.exceptionsStr.includes(fieldName)
          // @ts-ignore
        ? prevState[fieldName].filter((_, i) => i !== indexToRemove)
          // @ts-ignore
        : prevState[fieldName].filter((item: any) => item.id !== id);

      context.setAdmCashierStatisticReportFilter({
        ...context.admCashierStatisticReportFilter,
        // @ts-ignore
        [fieldName]: reportStorageProps.exceptionsStr.includes(fieldName) ? context.admCashierStatisticReportFilter[fieldName].filter((_, i) => i !== indexToRemove) : context.admCashierStatisticReportFilter[fieldName].filter((item: any) => item.id !== id),
      });

      //clear selectedFilterId
      setSelectedFilterId('');

      return {
        ...prevState,
        [fieldName]: newFieldData,
      }
    });

    setTimeout(() => {
      setGenerateReport((prevState) => {
        return !prevState
      });
    }, 100)
  }, [setUpdateFilter]);

  const collapseButton = (<>
    <Button
      type="button"
      variant="text"
      color="secondary"
      size="small"
      onClick={handleToggleCollapse}
      startIcon={openCollapse ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
    >
      {openCollapse
        ? t("common.components.filters.hide")
        : t("common.components.filters.show")}
    </Button>
  </>);

  return (
    <>
      <Box className={styles.block}>
        {!mobile && (
          <Box className={styles.first}>
            {!mobile && (<>
              { !!children && children }
            </>)}
          </Box>
        )}

        <Box className={styles.second}>
          <Box className={styles.actions}>
            {/*reset filter*/}
            <IconButton
              className={styles.iconButton}
              size={mobile ? 'small' : 'medium'}
              onClick={handleClearFilters}
              disabled={arePropsEqual({
                ...initialFilterData,
                sent_from: format(new Date(initialFilterData.sent_from), "yyyy-MM-dd HH:mm:ss"),
                sent_to: format(new Date(initialFilterData.sent_to), "yyyy-MM-dd HH:mm:ss"),
              }, {
                ...initialFilter,
                sent_from: format(new Date(initialFilter.sent_from), "yyyy-MM-dd HH:mm:ss"),
                sent_to: format(new Date(initialFilter.sent_to), "yyyy-MM-dd HH:mm:ss"),
              })}
            >
              <Tooltip title={t("common.buttons.reset_filters")} placement="bottom">
                <FilterAltOffIcon
                  color={arePropsEqual({
                    ...initialFilterData,
                    sent_from: format(new Date(initialFilterData.sent_from), "yyyy-MM-dd HH:mm:ss"),
                    sent_to: format(new Date(initialFilterData.sent_to), "yyyy-MM-dd HH:mm:ss"),
                  }, {
                    ...initialFilter,
                    sent_from: format(new Date(initialFilter.sent_from), "yyyy-MM-dd HH:mm:ss"),
                    sent_to: format(new Date(initialFilter.sent_to), "yyyy-MM-dd HH:mm:ss"),
                  }) ? 'inherit' : 'primary'}
                />
              </Tooltip>
            </IconButton>

            {/*open filter*/}
            <IconButton
              className={styles.iconButton}
              size={mobile ? 'small' : 'medium'}
              onClick={handleOpenDrawer}
            >
              <Tooltip title={t("common.components.filters.title")} placement="bottom">
                <Badge
                  className={styles.badge}
                  badgeContent={countValuesInObjWithExceptions(initialFilter, filterExceptions)}
                  color="secondary"
                >
                  <FilterAltIcon
                    color={!!countValuesInObjWithExceptions(initialFilter, filterExceptions) ? 'primary' : 'inherit'}
                  />
                </Badge>
              </Tooltip>
            </IconButton>

            {/*sort menu*/}
            {!!sortingColumns && mobile && (
              <SortMenu
                color="default"
                columns={sortingColumns}
                order={order}
                setOrder={setOrder}
                noMargin={true}
                prefix="columns_for_display.cashier.table.reports.columns."
              />
            )}

            {/*Favorites reports*/}
            <IconButton
              className={styles.iconButton}
              size={mobile ? 'small' : 'medium'}
              onClick={handleOpenFavoritesDrawer}
              disabled={!(!!savedReportsDefaultId && !!savedReportsData)}
            >
              <Tooltip title={t("common.components.saved_reports.title")} placement="bottom">
                <StarIcon />
              </Tooltip>
            </IconButton>

            {/*download*/}
            <PermissionsWrapper
              permissions={["api3.admin.cashierreportcontroller.download"]}
            >
              <IconButton
                className={styles.iconButton}
                size={mobile ? 'small' : 'medium'}
                onClick={onDownload}
              >
                <Tooltip title={t("common.buttons.download_report")} placement="bottom">
                  <DownloadIcon />
                </Tooltip>
              </IconButton>
            </PermissionsWrapper>
          </Box>
        </Box>
      </Box>

      {!mobile && (
        <Box className={styles.buttonWrapper}>
          <Box className={styles.buttonWithSwitcher}>
            <GenerateGroup
              filter={initialFilter}
              columns={columns}
              selectedMyReportId={selectedMyReportId}
              onGenerateReport={onGenerateReport}
              savedReportsData={savedReportsData}
              setSavedReportsData={setSavedReportsData}
              getSavedReports={getSavedReports}
              setFilter={setUpdateFilter}
            />
          </Box>

          {!!countValuesInObj(initialFilter) && collapseButton}
        </Box>
      )}

      {!mobile ? (
        <Collapse in={openCollapse} collapsedSize={0}>
          <Collapse in={showVisibleFilter} collapsedSize={0} timeout={100}>
            <VisibleFilter
              filter={initialFilter}
              setUpdateFilter={setUpdateFilter}
            />
          </Collapse>

          {!!countValuesInObjWithExceptions(initialFilter, filterExceptions) && (
            <SelectedFiltersWrapper
              initialFilter={initialFilter}
              setActiveField={handleSetActiveField}
              onDeleteOption={onDeleteOption}
            />
          )}
        </Collapse>
      ) : (
        <Collapse in={showVisibleFilter} collapsedSize={0} timeout={100}>
          <VisibleFilter
            filter={initialFilter}
            setUpdateFilter={setUpdateFilter}
          />
        </Collapse>
      )}

      <MuiDrawerWrapper
        title={t("common.components.filters.title")}
        open={openDrawer}
        toggleDrawerState={setOpenDrawer}
      >
        <FilterTabs
          activeField={activeField}
          initialFilter={initialFilter}
          defaultFilter={initialFilterData}
          setUpdateFilter={setUpdateFilter}
          setToggleDrawer={setOpenDrawer}
          setGenerateReport={setGenerateReport}
          selectedFilterId={selectedFilterId}
          setSelectedFilterId={setSelectedFilterId}
        />
      </MuiDrawerWrapper>

      <MuiDrawerWrapper
        title={t("common.components.saved_reports.title")}
        open={openFavoritesDrawer}
        toggleDrawerState={setOpenFavoritesDrawer}
      >
        <SavedReportsList
          setToggleDrawer={setOpenFavoritesDrawer}
          selectedMyReportId={selectedMyReportId}
          setSelectedMyReportId={setSelectedMyReportId}
          savedReportsData={savedReportsData}
          setSavedReportsData={setSavedReportsData}
          savedReportsFavoritesIds={savedReportsFavoritesIds}
          setSavedReportsFavoritesIds={setSavedReportsFavoritesIds}
          savedReportsDefaultId={savedReportsDefaultId}
          setSavedReportsDefaultId={setSavedReportsDefaultId}
          savedReportsDefaultItem={savedReportsDefaultItem}
          getSavedReports={getSavedReports}
          savedReportsFavoritesItem={savedReportsFavoritesItem}
          setUpdateFilter={setUpdateFilter}
          setColumns={setColumns}
          onGenerateReport={onGenerateReport}
          setGenerateReport={setGenerateReport}
          getSavedReportsDefault={getSavedReportsDefault}
          resetToDefault={resetToDefault}
        />
      </MuiDrawerWrapper>
    </>
  );
};

export default DrawerFilter;
